import type {
  CoreActivitiesCheckout,
  AgeBandSelection,
  ExperienceBookableItem,
  ExperiencesShopResponse,
  ExperiencesPriceBreakdown,
  IExperiencesAgeBandCounts,
  IExperiencesTravelerBandCounts,
  IExperiencesPassengerCounts,
  ExperiencesAgeBandEnum} from "redmond";

type CoreData = NonNullable<CoreActivitiesCheckout>;

export const mapCoreShopDetails = (
  core: CoreData,
): { response: ExperiencesShopResponse; activtyIndex: number } => {
  const {shopDetails} = core;

  const bookableItems: ExperienceBookableItem[] = shopDetails.bookableItems.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- core types from @capone/portal aren't available as named imports
    (item: any) => ({
      ...item,
      experienceOptionId: { value: item.activityOptionId ?? "" },
      description: item.description ?? "",
      datesPastSearchedRange: item.datesPastSearchedRange,
    }),
  );

  const examplePriceBreakdown: ExperiencesPriceBreakdown = {
    ...shopDetails.examplePriceBreakdown,
    optionId:
      shopDetails.examplePriceBreakdown.optionId != null
        ? { value: shopDetails.examplePriceBreakdown.optionId }
        : undefined,
  };

  const pickupLocations = shopDetails.bookingQuestions?.pickupLocations;
  const bookingQuestions = {
    ...shopDetails.bookingQuestions,
    pickupLocations: pickupLocations && {
      ...pickupLocations,
      locations: (pickupLocations.locations ?? []).map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- b2bportal names this field `activityLocation`; capone's PickupLocation expects `experienceLocation`
        (location: any) => ({
          ...location,
          experienceLocation: location.activityLocation,
        }),
      ),
    },
  };

  // Other known b2bportal/redmond field-name divergences on this response that are
  // NOT remapped because nothing in the checkout/book flow currently reads them:
  // - bookingRequirements.{max,min}Travelers (b2b) vs {max,min}Travellers (redmond)
  // - itinerary's discriminant key: ActivityItinerary (b2b) vs ExperienceItinerary (redmond)
  // - pricingCategory's discriminant key: ActivityPricingCategory (b2b) vs ExperiencePricingCategory (redmond)
  // If checkout logic ever starts reading one of these (e.g. wiring up
  // LocalTravelerSelectWorkflow's currently-unused maxTravelerCount prop), remap
  // it here the same way bookingQuestions.pickupLocations is remapped above —
  // don't assume the raw spread carries the right field name.

  const response: ExperiencesShopResponse = {
    ...shopDetails,
    id: { value: shopDetails.id },
    bookableItems,
    examplePriceBreakdown,
    bookingQuestions,
    // b2bportal uses `similarActivities`, capone uses `similarExperiences`
    similarExperiences: (shopDetails as any).similarActivities ?? [], // eslint-disable-line @typescript-eslint/no-explicit-any -- b2bportal field name differs from capone type
  };

  return { response, activtyIndex: 0 };
};

export const mapCorePriceBreakdowns = (
  core: CoreData,
): ExperiencesPriceBreakdown[] =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- core types from @capone/portal aren't available as named imports
  core.shopOptionSummary.priceBreakdowns.map((pb: any) => ({
    ...pb,
    optionId: pb.optionId != null ? { value: pb.optionId } : undefined,
    date: core.selectedDate,
  }));

export const mapCoreTravelerCounts = (
  core: CoreData,
): IExperiencesPassengerCounts => {
  const travelerBand = core.appliedTravelers.find(
    (t: AgeBandSelection) => (t.ageBand as string) === "Traveler",
  );

  if (travelerBand != null) {
    const counts: IExperiencesTravelerBandCounts = {
      travelerCount: travelerBand.numberOfTravelers,
    };
    return counts;
  }

  const counts: IExperiencesAgeBandCounts = {
    adultsCount: 0,
    childrenCount: 0,
    infantsCount: 0,
    seniorsCount: 0,
    youthsCount: 0,
  };

  core.appliedTravelers.forEach((traveler: AgeBandSelection) => {
    const band = traveler.ageBand as string as ExperiencesAgeBandEnum;
    switch (band) {
      case "Adult" as ExperiencesAgeBandEnum:
        counts.adultsCount = traveler.numberOfTravelers;
        break;
      case "Child" as ExperiencesAgeBandEnum:
        counts.childrenCount = traveler.numberOfTravelers;
        break;
      case "Infant" as ExperiencesAgeBandEnum:
        counts.infantsCount = traveler.numberOfTravelers;
        break;
      case "Senior" as ExperiencesAgeBandEnum:
        counts.seniorsCount = traveler.numberOfTravelers;
        break;
      case "Youth" as ExperiencesAgeBandEnum:
        counts.youthsCount = traveler.numberOfTravelers;
        break;
      default:
        break;
    }
  });

  return counts;
};

export const findCoreSelectedBookableItem = (
  mappedResponse: ExperiencesShopResponse,
  selectedBookableItemId: string | undefined,
): ExperienceBookableItem | null => {
  if (selectedBookableItemId == null) return null;
  return (
    mappedResponse.bookableItems.find(
      (item) => item.experienceOptionId.value === selectedBookableItemId,
    ) ?? null
  );
};
